import Carregando from '@/shared/components/Carregando';
import DefensorPorComarca from '../reports/DefensorPorComarca.report';
import PrintComponent from '@/shared/components/PrintComponent';
import React, { Fragment, useRef, useState, useEffect, useMemo } from 'react';
import RelatorioDefensoresContent from './tLotacoesPorComarca';
import { Button, Modal, Form, Tabs, Tab, Badge } from 'react-bootstrap';
import { EnumRelatorioLotacoes } from '@/modules/business/lotacoes/enums/enum.relatorio.lotacoes';
import { FullRelatorio } from '@/modules/reports/relatorio.service';
import { IFullReport, IReportComarca } from '@/api.types';
import { exportFromJsxToPdf } from '@/shared/utils/pdf';
import { toast } from 'react-toastify';

interface FilterModalProps {
  relatorio: IFullReport | null;
  selectedDefensores: string[];
  setSelectedDefensores: (defensores: string[]) => void;
  selectedDefensorias: string[];
  setSelectedDefensorias: (defensorias: string[]) => void;
  selectedComarcas: string[];
  setSelectedComarcas: (comarcas: string[]) => void;
  onClose: () => void;
}

const FilterModal: React.FC<FilterModalProps> = ({
  relatorio,
  selectedDefensores,
  setSelectedDefensores,
  selectedDefensorias,
  setSelectedDefensorias,
  selectedComarcas,
  setSelectedComarcas,
  onClose,
}) => {
  const [searchDefensores, setSearchDefensores] = useState('');
  const [searchDefensorias, setSearchDefensorias] = useState('');
  const [searchComarcas, setSearchComarcas] = useState('');

  const getComarcas = useMemo(() => {
    if (!relatorio) return [];
    return relatorio.comarcas.map((comarca: IReportComarca) =>
      `${comarca.prefixo || ''} ${comarca.nome}`.trim(),
    );
  }, [relatorio]);

  const getDefensorias = useMemo(() => {
    if (!relatorio) return [];
    return relatorio.comarcas
      .filter((comarca) => selectedComarcas.length === 0 || selectedComarcas.includes(`${comarca.prefixo || ''} ${comarca.nome}`.trim()))
      .flatMap((comarca: IReportComarca) => comarca.locais || [])
      .map((defensoria) => defensoria.nome)
      .filter((nome, index, self) => self.indexOf(nome) === index);
  }, [relatorio, selectedComarcas]);

  const getDefensoresComLotacao = useMemo(() => {
    if (!relatorio) return [];
    const hoje = new Date();

    const defensoriasFiltradas = relatorio.comarcas
      .filter(comarca =>
        selectedComarcas.length === 0 ||
        selectedComarcas.includes(`${comarca.prefixo || ''} ${comarca.nome}`.trim())
      )
      .flatMap(comarca => comarca.locais || [])
      .filter(defensoria =>
        selectedDefensorias.length === 0 ||
        selectedDefensorias.includes(defensoria.nome)
      );

    const defensoriaIds = new Set(defensoriasFiltradas.map(d => d.id));

    const lotacoesValidas = relatorio.lotacoes.filter(lotacao =>
      lotacao.defensor_id &&
      defensoriaIds.has(lotacao.defensoria_id) &&
      (!lotacao.data_inicial || new Date(lotacao.data_inicial) <= hoje) &&
      (!lotacao.data_final || new Date(lotacao.data_final) >= hoje)
    );

    const defensores = lotacoesValidas.map(lotacao => {
      const defensor = relatorio.pessoas.find(p => p.id === lotacao.defensor_id);
      return defensor ? [defensor.titulo?.trim(), defensor.nome?.trim()].filter(Boolean).join(' ') : null;
    }).filter((item): item is string => !!item);

    return [...new Set(defensores)];
  }, [relatorio, selectedComarcas, selectedDefensorias]);

  useEffect(() => {
    const validDefensorias = getDefensorias;
    setSelectedDefensorias(selectedDefensorias.filter((d) => validDefensorias.includes(d)));
  }, [getDefensorias, selectedDefensorias]);

  useEffect(() => {
    const validDefensores = getDefensoresComLotacao;
    setSelectedDefensores(selectedDefensores.filter((d) => validDefensores.includes(d)));
  }, [getDefensoresComLotacao, selectedDefensores]);

  const handleSelectAllDefensores = () => {
    const filteredDefensores = getDefensoresComLotacao.filter((defensor) =>
      defensor.toLowerCase().includes(searchDefensores.toLowerCase()),
    );
    setSelectedDefensores([...new Set([...selectedDefensores, ...filteredDefensores])]);
  };

  const handleDeselectAllDefensores = () => {
    setSelectedDefensores([]);
  };

  const handleCheckboxChangeDefensores = (defensor: string) => {
    setSelectedDefensores(
      selectedDefensores.includes(defensor)
        ? selectedDefensores.filter((d) => d !== defensor)
        : [...selectedDefensores, defensor]
    );
  };

  const handleSelectAllDefensorias = () => {
    const filteredDefensorias = getDefensorias.filter((defensoria) =>
      defensoria.toLowerCase().includes(searchDefensorias.toLowerCase()),
    );
    setSelectedDefensorias([...new Set([...selectedDefensorias, ...filteredDefensorias])]);
  };

  const handleDeselectAllDefensorias = () => {
    setSelectedDefensorias([]);
  };

  const handleCheckboxChangeDefensorias = (defensoria: string) => {
    setSelectedDefensorias(
      selectedDefensorias.includes(defensoria)
        ? selectedDefensorias.filter((d) => d !== defensoria)
        : [...selectedDefensorias, defensoria]
    );
  };

  const handleSelectAllComarcas = () => {
    const filteredComarcas = getComarcas.filter((comarca) =>
      comarca.toLowerCase().includes(searchComarcas.toLowerCase()),
    );
    setSelectedComarcas([...new Set([...selectedComarcas, ...filteredComarcas])]);
  };

  const handleDeselectAllComarcas = () => {
    setSelectedComarcas([]);
  };

  const handleCheckboxChangeComarcas = (comarca: string) => {
    setSelectedComarcas(
      selectedComarcas.includes(comarca)
        ? selectedComarcas.filter((c) => c !== comarca)
        : [...selectedComarcas, comarca]
    );
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px',
    padding: '10px',
    maxHeight: '50vh',
    overflowY: 'auto',
  };

  const filterTabTitle = (label: string, count: number) => (
    <span className="d-inline-flex align-items-center gap-1">
      {label}
      {count > 0 ? (
        <Badge bg="primary" pill>
          {count}
        </Badge>
      ) : null}
    </span>
  );

  return (
    <Modal
      show={true}
      onHide={onClose}
      size="xl"
      style={{ maxHeight: '100%', overflowY: 'auto' }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Filtros de Resultados</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs defaultActiveKey="defensor" id="filter-tabs" className="mb-3">
          <Tab eventKey="defensor" title={filterTabTitle('Defensores', selectedDefensores.length)}>
            <Form>
              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Buscar por nome do defensor..."
                  value={searchDefensores}
                  onChange={(e) => setSearchDefensores(e.target.value)}
                />
              </Form.Group>
              <div className="mb-3">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSelectAllDefensores}
                  className="me-2"
                >
                  Marcar Todos
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleDeselectAllDefensores}
                >
                  Desmarcar Todos
                </Button>
              </div>
              <div style={gridStyle}>
                {relatorio && getDefensoresComLotacao.length > 0 ? (
                  getDefensoresComLotacao
                    .filter((defensor: string) =>
                      defensor.toLowerCase().includes(searchDefensores.toLowerCase()),
                    )
                    .map((defensor: string, index: number) => (
                      <Form.Check
                        key={`defensor-${index}`}
                        id={`defensor-${index}`}
                        type="checkbox"
                        label={defensor}
                        checked={selectedDefensores.includes(defensor)}
                        onChange={() => handleCheckboxChangeDefensores(defensor)}
                        style={{ margin: 0 }}
                      />
                    ))
                ) : (
                  <p>Nenhum defensor disponível para as comarcas e defensorias selecionadas.</p>
                )}
              </div>
            </Form>
          </Tab>
          <Tab eventKey="defensoria" title={filterTabTitle('Defensorias', selectedDefensorias.length)}>
            <Form>
              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Buscar por nome da defensoria..."
                  value={searchDefensorias}
                  onChange={(e) => setSearchDefensorias(e.target.value)}
                />
              </Form.Group>
              <div className="mb-3">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSelectAllDefensorias}
                  className="me-2"
                >
                  Marcar Todos
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleDeselectAllDefensorias}
                >
                  Desmarcar Todos
                </Button>
              </div>
              <div style={gridStyle}>
                {relatorio && getDefensorias.length > 0 ? (
                  getDefensorias
                    .filter((defensoria: string) =>
                      defensoria.toLowerCase().includes(searchDefensorias.toLowerCase()),
                    )
                    .map((defensoria: string, index: number) => (
                      <Form.Check
                        key={`defensoria-${index}`}
                        id={`defensoria-${index}`}
                        type="checkbox"
                        label={defensoria}
                        checked={selectedDefensorias.includes(defensoria)}
                        onChange={() => handleCheckboxChangeDefensorias(defensoria)}
                        style={{ margin: 0 }}
                      />
                    ))
                ) : (
                  <p>Nenhuma defensoria disponível para as comarcas selecionadas.</p>
                )}
              </div>
            </Form>
          </Tab>
          <Tab eventKey="comarca" title={filterTabTitle('Comarcas', selectedComarcas.length)}>
            <Form>
              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Buscar por nome da comarca..."
                  value={searchComarcas}
                  onChange={(e) => setSearchComarcas(e.target.value)}
                />
              </Form.Group>
              <div className="mb-3">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSelectAllComarcas}
                  className="me-2"
                >
                  Marcar Todos
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleDeselectAllComarcas}
                >
                  Desmarcar Todos
                </Button>
              </div>
              <div style={gridStyle}>
                {relatorio && getComarcas.length > 0 ? (
                  getComarcas
                    .filter((comarca: string) =>
                      comarca.toLowerCase().includes(searchComarcas.toLowerCase()),
                    )
                    .map((comarca: string, index: number) => (
                      <Form.Check
                        key={`comarca-${index}`}
                        id={`comarca-${index}`}
                        type="checkbox"
                        label={comarca}
                        checked={selectedComarcas.includes(comarca)}
                        onChange={() => handleCheckboxChangeComarcas(comarca)}
                        style={{ margin: 0 }}
                      />
                    ))
                ) : (
                  <p>Nenhuma comarca disponível.</p>
                )}
              </div>
            </Form>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
        <Button variant="primary" onClick={onClose}>
          Aplicar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default function RelatorioDefensoresHeaderPublic() {
  const contentRef = useRef<HTMLDivElement>(null);

  const [agruparComarca] = useState(true);
  const [agruparPorPredio] = useState(false);
  const [agruparPorDiretoria] = useState(false);
  const [agruparAtuacao] = useState(true);
  const [exibirEstagiarios] = useState(false);
  const selecionado = EnumRelatorioLotacoes.Defensores_Comarca;
  const [showModal, setShowModal] = useState<boolean>(false);
  const [relatorio, setRelatorio] = useState<IFullReport | null>(null);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState<{
    defensores: string[];
    defensorias: string[];
    comarcas: string[];
  }>({
    defensores: [],
    defensorias: [],
    comarcas: [],
  });

  useEffect(() => {
    const savedFilters = localStorage.getItem('lotacaoFilters');
    if (savedFilters) {
      setFilters(JSON.parse(savedFilters));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('lotacaoFilters', JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    setLoading(true);
    FullRelatorio(agruparAtuacao, agruparComarca)
      .then((rf) => {
        setRelatorio({
          nome: rf?.nome || '',
          editadoem: rf?.editadoem ? (typeof rf.editadoem === 'string' ? rf.editadoem : new Date(rf.editadoem).toISOString()) : new Date().toISOString(),
          comarcas: rf?.comarcas || [],
          pessoas: rf?.pessoas || [],
          lotacoes: rf?.lotacoes || [],
          atuacoes: rf?.atuacoes || [],
          varas: rf?.varas || [],
        } as IFullReport);
      })
      .catch((error) => {
        toast.error(String(error));
        setRelatorio(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [agruparComarca, agruparAtuacao]);

  function GetComponente() {
    if (loading) return <Carregando />;
    if (!relatorio) return <div>Erro ao carregar o relatório. Tente novamente.</div>;

    switch (selecionado) {
      case EnumRelatorioLotacoes.Defensores_Comarca:
        return (
          <RelatorioDefensoresContent
            agrupar={agruparComarca}
            datasValidas={agruparAtuacao}
            exibirEventos={false}
            exibirEstagiarios={exibirEstagiarios}
            relatorio={relatorio}
            selectedDefensores={filters.defensores}
            selectedDefensorias={filters.defensorias}
            selectedComarcas={filters.comarcas}
            agruparPorPredio={agruparPorPredio}
            agruparPorDiretoria={agruparPorDiretoria}
          />
        );
      default:
        return null;
    }
  }

  function handleImprimir() {
    if (!relatorio) {
      toast.error('Relatório não carregado. Tente novamente.');
      return;
    }

    switch (selecionado) {
      case EnumRelatorioLotacoes.Defensores_Comarca:
        exportFromJsxToPdf(
          <DefensorPorComarca
            relatorio={relatorio}
            exibirEstagiarios={exibirEstagiarios}
            exibirEventos={false}
            agruparAtuacao={agruparAtuacao}
            agruparPorPredio={agruparPorPredio}
            agruparPorDiretoria={agruparPorDiretoria}
            agruparComarca={agruparComarca}
            selectedComarcas={filters.comarcas}
            selectedDefensorias={filters.defensorias}
            selectedDefensores={filters.defensores}
          />
        );
        break;
      default:
        break;
    }
  }

  return (
    <Fragment>
      <div className="row">
        <div className="col-12 text-center">
          <div className="d-inline-block me-2" title="Imprimir Tabela de Lotação">
            <button type="button" className="btn btn-outline-secondary" onClick={handleImprimir} >
              Imprimir
            </button>
          </div>

          <div className="d-inline-block" title="Filtros aplicados após pesquisa.">
            <button className="btn btn-outline-success" onClick={() => setShowModal(true)} >
              Filtros
            </button>
          </div>
        </div>
      </div>

      <div className="row pt-5">
        <PrintComponent ref={contentRef} value={GetComponente()} />
      </div>

      {showModal && (
        <FilterModal
          relatorio={relatorio}
          selectedDefensores={filters.defensores}
          setSelectedDefensores={(defensores) => setFilters({ ...filters, defensores })}
          selectedDefensorias={filters.defensorias}
          setSelectedDefensorias={(defensorias) => setFilters({ ...filters, defensorias })}
          selectedComarcas={filters.comarcas}
          setSelectedComarcas={(comarcas) => setFilters({ ...filters, comarcas })}
          onClose={() => setShowModal(false)}
        />
      )}
    </Fragment>
  );
}

